/* eslint-disable react/display-name */
import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Transition, animated } from 'react-spring/renderprops.cjs';
import get from 'lodash/get';
import cn from 'classnames';
// components
import { normalizeDataForCart } from 'src/containers/AddSkuPage/QuestionsFlowPage/Parts/QAPage/utils';
import { UISelectionDisplay } from 'src/containers/AddSkuPage/QuestionsFlowPage/Parts/QAPage';
import { QuestionsHeader } from 'src/containers/AddSkuPage/QuestionsFlowPage/Parts';
import { ModalV2 } from 'src/components/HTKit/Modals/ModalV2';
import { FlyAwayModal } from 'HTKit/Modals/FlyAwayModal';
// selectors
import componentSelectors from 'src/containers/AddSkuPage/QuestionsFlowPage/Parts/QAPage/selectors';
// constants & helpers
import { ANIMATION_DIRECTION } from 'src/containers/AddSkuPage/QuestionsFlowPage/constants';
import { push } from 'src/utils/paths';
import ROUTES from 'src/routes';
import useOutsideClick from 'src/hooks/useOutsideClick';
// context
import { useQAContext } from 'src/containers/AddSkuPage/QuestionsFlowPage/Context';
// actions
import {
  updateToNewQA,
  pageLevelErrorAction,
  addBaseSkuInformation,
  goBack,
} from 'src/containers/AddSkuPage/QuestionsFlowPage/actions';
import { addToCartQFlow } from 'src/containers/AddSkuPage/actions';
// styles
import styles from 'src/containers/AddSkuPage/QuestionsFlowPage/styles.scss';

const QuestionAnswerDisplay = ({
  DATA = {},
  skuBasePrice,
  skuName,
  previousLocation,
  push: pushPath,
  seoName,
  addToCartQFlow: addToCartQFlowAction,
}) => {
  const [contextState, contextDispatch] = useQAContext();
  const [modalInfo, setModalInfo] = useState({});
  const currentQuestionFlow = DATA[contextState.currentQuestion] || {};
  const { additonalInfo = {} } = DATA;
  const animation = contextState.flowComplete
    ? ANIMATION_DIRECTION.completed
    : ANIMATION_DIRECTION[contextState.direction];
  const showModal = Boolean(modalInfo.content);

  function listenForPopState(e) {
    const { state } = e;
    if (state !== null) {
      const currentSearch = parseInt(get(window, 'location.search', '').replace(/\D+/g, ''), 10);

      /*
       * Go back if url is less than what we saved, or url is greater than our questions array
       * If go from 5 to 4 and hit back, it goes to 5. Browser history doesn't care about dynamic flow
       * */
      if (
        currentSearch <= contextState.currentSearch ||
        currentSearch > contextState.questionPath.length
      ) {
        contextDispatch(goBack());
      }
    }
  }

  // helper
  const eventerPopState = (type) => window[`${type}EventListener`]('popstate', listenForPopState);

  /**
   * If we have our initial load, lets "seed" it with the first question. Since this
   * could be different for each sku set, we set it in seedQuestion.
   */
  useEffect(() => {
    if (!contextState.currentQuestion) {
      if (DATA.error) contextDispatch(pageLevelErrorAction(DATA.error));
      contextDispatch(addBaseSkuInformation({ skuBasePrice, skuName, maxFlow: DATA.maxFlow }));
      contextDispatch(updateToNewQA(DATA[DATA.seedQuestion]));
    }
  }, []);

  /**
   * We set "redirect" in the reducer when the user tries to go to a question less than 1,
   * otherwise lets move the user forward in the question flow.
   */
  useEffect(() => {
    if (contextState.redirect) {
      pushPath(`${ROUTES.TECH_SUPPORT}${seoName}`);
    } else {
      pushPath(`${previousLocation.pathname}?question${contextState.currentSearch}`);
    }
  }, [contextState.questionPath.length, contextState.redirect]);

  /**
   * Start our "back button" listener. each new question, lets reinstantiate. Otherwise, didn't pick
   * up the changes.
   */
  useEffect(() => {
    eventerPopState('add');

    return () => eventerPopState('remove');
  }, [contextState.currentSearch]);

  /**
   * Our last step has been submitted. Call add-to-cart action
   */
  useEffect(() => {
    if (contextState.flowComplete) {
      addToCartQFlowAction({
        completedAnswers: normalizeDataForCart(contextState.completedAnswers),
      });
    }
  }, [contextState.flowComplete]);

  function launchAdditionalInfoDisplay(id, defaultID) {
    const ref = get(additonalInfo, id, get(additonalInfo, defaultID));
    if (ref) {
      setModalInfo(ref);
    }
  }
  const wrapperRef = useRef(null);
  useOutsideClick(wrapperRef, launchAdditionalInfoDisplay);

  return (
    <div className={styles.animationContainer} id="AddSkuAnimationDiv">
      <Transition
        native
        unique
        items={currentQuestionFlow.id || 'completed'}
        onFrame={() => window.scroll(0, 0)}
        from={{ opacity: 0, transform: `translate3d(${animation.from}, 0 ,0)` }}
        enter={{ opacity: 1, transform: `translate3d(${animation.enter}, 0, 0)` }}
        leave={{ opacity: 0, transform: `translate3(${animation.leave}, 0, 0)` }}
      >
        {() => (style) => (
          <animated.div style={{ ...style }}>
            <QuestionsHeader
              alert={currentQuestionFlow.alert}
              question={currentQuestionFlow.label}
              hint={currentQuestionFlow.hint}
            />
            <UISelectionDisplay
              launchAdditionalInfoDisplay={launchAdditionalInfoDisplay}
              DATA={DATA}
              previousLocation={previousLocation}
            />
          </animated.div>
        )}
      </Transition>
      <ModalV2
        isVisible={showModal}
        hide={() => setModalInfo({})}
        modalClassName={styles.desktopModal}
        header={modalInfo.label}
        footerClasses={styles.modalFooter}
      >
        <div className="flex flexDirectionColumn">
          {modalInfo.imageRef && <img src={modalInfo.imageRef} className={styles.modalImage} />}
          <p className={cn(styles.modalContent, 'p1 n800')}>{modalInfo.content}</p>
        </div>
      </ModalV2>
      <FlyAwayModal
        isVisible={showModal}
        hide={() => setModalInfo({})}
        header={modalInfo.label}
        modalClassName={styles.mobileModal}
      >
        <div className="flex flexDirectionColumn">
          {modalInfo.imageRef && <img src={modalInfo.imageRef} className={styles.modalImage} />}
          <p className={cn(styles.modalContent, 'p1 n800')}>{modalInfo.content}</p>
        </div>
      </FlyAwayModal>
    </div>
  );
};

QuestionAnswerDisplay.propTypes = {
  DATA: PropTypes.object.isRequired,
  skuName: PropTypes.string.isRequired,
  skuBasePrice: PropTypes.number.isRequired,
  skuId: PropTypes.number.isRequired,
  addToCartQFlow: PropTypes.func.isRequired,
  push: PropTypes.func.isRequired,
  replace: PropTypes.func.isRequired,
  seoName: PropTypes.string.isRequired,
  previousLocation: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
};

function mapDispatchToProps(dispatch) {
  return {
    push: (...args) => dispatch(push(...args)),
    addToCartQFlow: (...args) => dispatch(addToCartQFlow(...args)),
  };
}

export default connect(componentSelectors, mapDispatchToProps)(QuestionAnswerDisplay);
